import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, Check, X } from "lucide-react";
import { useTheme, THEMES } from "../../context/ThemeContext";
import PropTypes from "prop-types";

const ThemePreview = ({ themeId, theme, isActive, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(themeId);
  }, [onClick, themeId]);

  return (
    <motion.button
      onClick={handleClick}
      className="relative w-full text-left rounded-xl overflow-hidden border-2 transition-all duration-200"
      style={{
        borderColor: isActive ? theme.colors.accent : theme.colors.border,
        backgroundColor: theme.colors.surface,
      }}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Color preview strip */}
      <div className="h-16 flex">
        <div
          className="flex-1"
          style={{ backgroundColor: theme.colors.primary }}
        />
        <div
          className="flex-1"
          style={{ backgroundColor: theme.colors.secondary }}
        />
        <div
          className="flex-1"
          style={{ backgroundColor: theme.colors.accent }}
        />
        <div
          className="flex-1"
          style={{ backgroundColor: theme.colors.background }}
        />
      </div>

      {/* Theme info */}
      <div
        className="p-3 flex items-center justify-between"
        style={{ backgroundColor: theme.colors.surface }}
      >
        <div>
          <p
            className="font-semibold text-sm"
            style={{ color: theme.colors.text }}
          >
            {theme.name}
          </p>
          <p
            className="text-xs"
            style={{ color: theme.colors.textMuted }}
          >
            {themeId === "navy"
              ? "Professional"
              : themeId === "ocean"
                ? "Calm & Focused"
                : themeId === "forest"
                  ? "Nature & Growth"
                  : themeId === "sunset"
                    ? "Creative & Energetic"
                    : "Dark Mode"}
          </p>
        </div>

        {isActive && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: theme.colors.accent }}
          >
            <Check size={14} style={{ color: theme.colors.textInverse }} />
          </motion.div>
        )}
      </div>
    </motion.button>
  );
};

ThemePreview.propTypes = {
  themeId: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const ThemeSwitcher = ({ variant = "button" }) => {
  const { theme, themeId, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleThemeSelect = useCallback(
    (selectedThemeId) => {
      setTheme(selectedThemeId);
      setIsOpen(false);
    },
    [setTheme]
  );

  const themeEntries = useMemo(() => Object.entries(THEMES), []);

  // Compact button variant (for header/toolbar)
  if (variant === "button") {
    return (
      <div className="relative">
        <motion.button
          onClick={handleToggle}
          className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200"
          style={{
            backgroundColor: theme.colors.surfaceAlt,
            color: theme.colors.text,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Palette size={18} />
          <span className="text-sm font-medium hidden sm:inline">
            {theme.name}
          </span>
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={handleClose}
              />

              {/* Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 top-full mt-2 w-64 rounded-xl shadow-xl z-50 p-3"
                style={{
                  backgroundColor: theme.colors.surface,
                  boxShadow: theme.shadows.cardHover,
                }}
              >
                <div className="flex items-center justify-between mb-3 pb-2 border-b" style={{ borderColor: theme.colors.border }}>
                  <span className="font-semibold text-sm" style={{ color: theme.colors.text }}>
                    Choose Theme
                  </span>
                  <button
                    onClick={handleClose}
                    className="p-1 rounded-full hover:opacity-70 transition-opacity"
                    style={{ color: theme.colors.textMuted }}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {themeEntries.map(([id, themeData]) => (
                    <ThemePreview
                      key={id}
                      themeId={id}
                      theme={themeData}
                      isActive={themeId === id}
                      onClick={handleThemeSelect}
                    />
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Inline variant (for settings page)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {themeEntries.map(([id, themeData]) => (
        <ThemePreview
          key={id}
          themeId={id}
          theme={themeData}
          isActive={themeId === id}
          onClick={handleThemeSelect}
        />
      ))}
    </div>
  );
};

ThemeSwitcher.propTypes = {
  variant: PropTypes.oneOf(["button", "inline"]),
};

export default ThemeSwitcher;
